import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Download } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BrandPeriod, DEMAND_DIMENSIONS, DemandDimension, DemandRow, useBrandApi } from '../../../common/hooks/use-brand-api.hook';
import { PillTabs, SectionCard, formatNumber } from '../../../components/design-system/admin';
import { BarList, Thumb, TrendChart } from '../../../components/design-system/analytics';
import { SortDirection, SortHeader, sortRows } from '../../../components/design-system/common';
import { useLoader } from '../../../modules/vendor/analytics/viz/use-loader';
import { BrandPage, Change, LoadFailed, PeriodPills, PrivacyFootnote, SuppressedNotice, useBrandMe } from '../../../modules/brand/components/BrandUi';
import { OTHER_KEY, dayLabel, rowLabel, saveCsv } from '../../../modules/brand/brand-format';

type SortKey = 'label' | 'visualizations' | 'shoppers' | 'favourites' | 'buyClicks' | 'leads' | 'renders' | 'downloads' | 'retailers';

const isDimension = (v: string | null): v is DemandDimension => !!v && (DEMAND_DIMENSIONS as readonly string[]).includes(v);

/**
 * Demand, cut any way a brand asks: by product or collection, by where the
 * shoppers are, by the room, by colour, look or format, or week by week —
 * as a chart, a sortable table and a CSV. Rows the privacy rules cannot show
 * on their own are in "Other".
 */
export function BrandDemandPage() {
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || 'en').slice(0, 2);
    const api = useBrandApi();
    const { me } = useBrandMe();
    const [params, setParams] = useSearchParams();
    const groupBy: DemandDimension = isDimension(params.get('groupBy')) ? (params.get('groupBy') as DemandDimension) : 'product';
    const [period, setPeriod] = useState<BrandPeriod>('30d');
    const [sort, setSort] = useState<{ key: SortKey; dir: SortDirection }>({ key: 'visualizations', dir: 'desc' });
    const [exporting, setExporting] = useState(false);

    const { data, loading, error, reload } = useLoader(async () => (me?.connected ? api.demand(period, groupBy) : null), [period, groupBy, me?.connected]);

    const label = (r: DemandRow) => rowLabel(t, groupBy, r, lang);
    const rows = useMemo(() => {
        const list = data?.rows ?? [];
        if (groupBy === 'week' && sort.key === 'label') return sortRows(list, 'key', sort.dir);
        return sortRows(list, sort.key === 'label' ? (r: DemandRow) => label(r) : sort.key, sort.dir);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, sort, groupBy, lang]);

    const exportCsv = async () => {
        setExporting(true);
        try {
            saveCsv(await api.demandCsv(period, groupBy), `${me?.brand?.slug ?? 'brand'}-demand-${groupBy}-${period}.csv`);
        } catch {
            toast.error(t('BRAND_PORTAL.DEMAND.EXPORT_FAILED'));
        } finally {
            setExporting(false);
        }
    };

    const head = (key: SortKey, text: string, end = true) => (
        <TableHead className={cn(end && 'text-end')}>
            <SortHeader column={key} sort={sort} onSort={setSort} align={end ? 'end' : 'start'}>
                {text}
            </SortHeader>
        </TableHead>
    );
    const leads = data?.leadsAvailable !== false;
    const cell = (r: DemandRow, other = false) => (
        <TableRow key={r.key} className={cn(other && 'text-muted-foreground')} data-testid={other ? 'brand-demand-other' : undefined}>
            <TableCell className="max-w-[280px]">
                {groupBy === 'product' && r.product ? (
                    <Link to={`/brand/products/${r.product.uuid}`} className="flex items-center gap-2 hover:underline">
                        <Thumb src={r.product.thumbnailUrl} size="xs" />
                        <span className="truncate">{label(r)}</span>
                    </Link>
                ) : (
                    <span className="truncate">
                        {label(r)}
                        {other && r.folded ? <span className="ms-1 text-xs">({t('BRAND_PORTAL.DEMAND.FOLDED', { count: r.folded })})</span> : null}
                    </span>
                )}
            </TableCell>
            <TableCell className="text-end tabular-nums">
                <span className="inline-flex items-center gap-1.5">
                    {formatNumber(r.visualizations)}
                    <Change value={r.change.visualizations} />
                </span>
            </TableCell>
            <TableCell className="text-end tabular-nums">{formatNumber(r.shoppers)}</TableCell>
            <TableCell className="text-end tabular-nums">{formatNumber(r.favourites)}</TableCell>
            <TableCell className="text-end tabular-nums">{formatNumber(r.buyClicks)}</TableCell>
            {leads && <TableCell className="text-end tabular-nums">{formatNumber(r.leads)}</TableCell>}
            <TableCell className="text-end tabular-nums">{formatNumber(r.renders)}</TableCell>
            <TableCell className="text-end tabular-nums">{formatNumber(r.downloads)}</TableCell>
            <TableCell className="text-end tabular-nums">{formatNumber(r.retailers)}</TableCell>
        </TableRow>
    );

    return (
        <BrandPage
            testId="brand-demand"
            crumb={t('BRAND_PORTAL.NAV.DEMAND')}
            title={t('BRAND_PORTAL.DEMAND.TITLE')}
            description={t('BRAND_PORTAL.DEMAND.SUBTITLE')}
            actions={
                <>
                    <PeriodPills value={period} onChange={setPeriod} />
                    <Button variant="outline" size="sm" onClick={exportCsv} disabled={exporting || !data || !!data.suppressed} data-testid="brand-demand-export">
                        <Download className="size-4" />
                        {t('BRAND_PORTAL.DEMAND.EXPORT')}
                    </Button>
                </>
            }
        >
            <PillTabs
                value={groupBy}
                onChange={(g) => {
                    setParams({ groupBy: g }, { replace: true });
                    setSort({ key: g === 'week' ? 'label' : 'visualizations', dir: g === 'week' ? 'asc' : 'desc' });
                }}
                ariaLabel={t('BRAND_PORTAL.DEMAND.GROUP_BY')}
                options={DEMAND_DIMENSIONS.map((d) => ({ value: d, label: t(`BRAND_PORTAL.DIMENSION.${d.toUpperCase()}`) }))}
            />

            {error && <LoadFailed onRetry={reload} />}
            {data?.suppressed ? (
                <SuppressedNotice reason={data.suppressed.reason} />
            ) : (
                !error && (
                    <>
                        <SectionCard title={t('BRAND_PORTAL.DEMAND.CHART_TITLE', { dimension: t(`BRAND_PORTAL.DIMENSION.${groupBy.toUpperCase()}`).toLowerCase() })} description={t('BRAND_PORTAL.DEMAND.CHART_TEXT')} testId="brand-demand-chart">
                            {groupBy === 'week' ? (
                                <div dir="ltr">
                                    <TrendChart
                                        data={sortRows(data?.rows ?? [], 'key', 'asc').map((r) => ({ label: dayLabel(r.key, lang), value: r.visualizations, previous: r.previous?.visualizations ?? null }))}
                                        valueLabel={t('BRAND_PORTAL.KPI.VISUALIZATIONS')}
                                        previousLabel={t('BRAND_PORTAL.COMMON.PREVIOUS_PERIOD')}
                                        height={220}
                                    />
                                </div>
                            ) : (
                                <BarList
                                    emptyText={loading ? undefined : t('BRAND_PORTAL.DEMAND.EMPTY')}
                                    items={(data?.rows ?? []).slice(0, 10).map((r) => ({
                                        key: r.key,
                                        label: label(r),
                                        value: r.visualizations,
                                        leading: groupBy === 'product' ? <Thumb src={r.product?.thumbnailUrl} size="xs" /> : undefined,
                                        secondary: <Change value={r.change.visualizations} />,
                                        href: groupBy === 'product' && r.product ? `/brand/products/${r.product.uuid}` : undefined,
                                    }))}
                                />
                            )}
                        </SectionCard>

                        <SectionCard flush title={t('BRAND_PORTAL.DEMAND.TABLE_TITLE')} description={leads ? undefined : t('BRAND_PORTAL.DEMAND.NO_LEADS_HERE')} testId="brand-demand-table">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        {head('label', t(`BRAND_PORTAL.DIMENSION.${groupBy.toUpperCase()}`), false)}
                                        {head('visualizations', t('BRAND_PORTAL.KPI.VISUALIZATIONS'))}
                                        {head('shoppers', t('BRAND_PORTAL.KPI.SHOPPERS'))}
                                        {head('favourites', t('BRAND_PORTAL.KPI.FAVOURITES'))}
                                        {head('buyClicks', t('BRAND_PORTAL.KPI.BUY_CLICKS'))}
                                        {leads && head('leads', t('BRAND_PORTAL.KPI.LEADS'))}
                                        {head('renders', t('BRAND_PORTAL.KPI.RENDERS'))}
                                        {head('downloads', t('BRAND_PORTAL.KPI.DOWNLOADS'))}
                                        {head('retailers', t('BRAND_PORTAL.DEMAND.RETAILERS'))}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {rows.map((r) => cell(r))}
                                    {data?.other && cell({ ...data.other, key: OTHER_KEY }, true)}
                                    {!loading && !rows.length && !data?.other && (
                                        <TableRow>
                                            <TableCell colSpan={9} className="py-8 text-center text-sm text-muted-foreground">
                                                {t('BRAND_PORTAL.DEMAND.EMPTY')}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </SectionCard>
                        <PrivacyFootnote />
                    </>
                )
            )}
        </BrandPage>
    );
}

export default BrandDemandPage;
